import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useGetCurrentUser, useIsUserReady } from '@front/auth/src/authentication-context';

import { CATEGORIES } from '@front/common/src/constants/universes';
import { isMassageReference } from '@front/common/src/utils/universes';
import { createBasketContentFromPrestations } from '@front/common/src/utils/prestations';
import { setBasketAttribute } from '@front/common/src/ducks/common/basket';
import { hasInvalidProChoices } from '@front/common/src/utils/previous-pros';
import { addOrRemovePrestation } from '@front/common/src/utils/basket';
import { BasketState } from '@front/common/src/ducks/customer/types';
import {
  Address,
  BasketContent,
  CategoryReference,
  GiftCard,
  PrestationReference,
  ProGender,
  Universe,
  UniverseReference,
} from '@front/common/src/types/commons';
import { ReferringProParams } from '@front/common/src/types/customer';
import { useExperiment } from '@front/common/src/providers/experiment-provider';
import { getMessages } from '@front/common/src/utils/errors';
import { ConfigContext } from '@front/common/src/providers/config-provider';
import { UniversesContext } from '@front/common/src/providers/universes-provider';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { useFetchGift } from '@front/customer/src/queries/gifts-queries';

import useQuery from '@front/web-ui/src/hooks/use-query';

import ducks from '../../ducks';
import ErrorPage from '../../components/error-page/error-page';
import { RootState } from '../../store';
import { signInUser, logoutUser } from '../../utils/tracking';

export const GIFT_CARD_FETCH_TEST_ID = 'gift-card-data-loaded';

const initializeBasketContent = (category: CategoryReference, prestation: PrestationReference): BasketContent => [
  {
    reference: category,
    prestations: addOrRemovePrestation(0, prestation, true, [
      {
        reference: category,
        prestations: [],
      },
    ]),
  },
];

type StateProps = {
  basketUniverse: BasketState['universe'];
  proChoices: BasketState['proChoices'];
  basketContent: BasketState['basketContent'];
};

type DispatchProps = {
  setBasketContent: (basketContent: BasketContent) => void;
  resetBasket: (universe?: UniverseReference) => void;
  setBasketAddress: (address: Partial<Address>) => void;
  setDuration: (duration: BasketState['duration']) => void;
  setWeeklyRecurrence: (weeklyRecurrence: BasketState['weekly_recurrence']) => void;
  setProGender: (gender: ProGender) => void;
  setReferringProParams: (params: ReferringProParams) => void;
  resetPromotion: () => void;
  setBasketGiftCard: (giftCard: GiftCard) => void;
  setUniverses: (universes: Universe[]) => void;
};

export type LoaderProps = StateProps & DispatchProps & PropsWithChildren;

export const Loader = ({
  children,
  resetBasket,
  basketUniverse,
  setBasketContent,
  setBasketAddress,
  setDuration,
  setWeeklyRecurrence,
  setProGender,
  setReferringProParams,
  proChoices,
  basketContent,
  resetPromotion,
  setBasketGiftCard,
  setUniverses,
}: LoaderProps) => {
  const {
    category,
    prestation,
    gift_code: giftCardCode,
    referring_pro: referringPro,
    referring_pro_name: referringProName,
    referring_pro_gender: referringProGender,
    universe,
  } = useQuery();
  const [initialized, setInitialized] = useState(false);
  const { loginExperimentUser, logoutExperimentUser } = useExperiment();
  const isUserReady = useIsUserReady();
  const user = useGetCurrentUser();
  const currentUniverse =
    import.meta.env.MODE === 'development' ? universe || window.wecasa.universe : window.wecasa.universe;
  const shouldFetchGiftCard = !!giftCardCode;
  const { isLoadingConfig, configError } = useContext(ConfigContext);
  const { isLoadingUniverses, universesError, universes } = useContext(UniversesContext);

  const { giftCard, giftCardError } = useFetchGift({
    // The return type of qs.useQuery for a query is string | string[] | ParsedQs | ParsedQs[] | undefined.
    // As we expect a string, we need to force the type
    code: giftCardCode as GiftCard['code'],
    options: { enabled: shouldFetchGiftCard },
  });
  const hasFetchedGift = !shouldFetchGiftCard || !!giftCard || !!giftCardError;

  useEffect(() => {
    if (universes) setUniverses(universes);
  }, [setUniverses, universes]);

  useEffect(() => {
    if (user?.public_id) {
      signInUser(user.public_id);
      loginExperimentUser({ userId: user.public_id, countryCode: getCurrentCountry() });
    } else {
      logoutUser();
      logoutExperimentUser();
    }
  }, [user, loginExperimentUser, logoutExperimentUser]);

  useEffect(() => {
    if (hasInvalidProChoices(proChoices)) {
      resetBasket();
    }

    if (!!currentUniverse && basketUniverse !== currentUniverse) {
      resetBasket(currentUniverse as UniverseReference);
      if (category && prestation) {
        setBasketContent(initializeBasketContent(category as CategoryReference, prestation as PrestationReference));
      }

      if (isMassageReference(currentUniverse as UniverseReference)) {
        setProGender('dont_care');
      }
    } else if (basketContent.length === 0 && category && prestation) {
      setBasketContent(initializeBasketContent(category as CategoryReference, prestation as PrestationReference));
    }

    if (referringPro) {
      setReferringProParams({
        slug: referringPro as string,
        name: referringProName as string,
        gender: referringProGender as string,
      });
    }

    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (giftCardCode && !!giftCard && !!universes && 'zip_code' in giftCard) {
      const { zip_code: zipCode, universe: giftUniverse, prestations } = giftCard;

      setBasketGiftCard(giftCard);
      setBasketAddress({ zip_code: zipCode });

      // If there's already a promo, delete the promo.
      // As gift cards and promos cannot be used at the same time.
      resetPromotion();

      if (!!giftUniverse && giftUniverse === basketUniverse) {
        if (isMassageReference(giftUniverse) && prestations?.length) {
          setProGender('dont_care');
          /**
           * We always select the woman category as default.
           * A gift card prestation could be existing in the woman category and man category
           * at the same time. So the user would need to change the category manually in this case
           * on the categories screen.
           */
          setBasketContent([{ reference: CATEGORIES.MASSAGE_WOMAN, prestations: [...prestations] }]);
        } else if (prestations?.length) {
          setBasketContent(
            createBasketContentFromPrestations(
              prestations,
              // We expect the universe to be defined
              universes.find((u: Universe) => u.reference === giftUniverse)!,
            ),
          );
        }
      }
    }
  }, [
    giftCardCode,
    giftCard,
    universes,
    setBasketAddress,
    setDuration,
    setBasketContent,
    setProGender,
    setWeeklyRecurrence,
    basketUniverse,
    resetPromotion,
    setBasketGiftCard,
  ]);

  if (!initialized || !isUserReady || isLoadingConfig || isLoadingUniverses) return null;

  if (!!universesError || !!configError || !!giftCardError) {
    return (
      <ErrorPage
        testId={hasFetchedGift ? GIFT_CARD_FETCH_TEST_ID : undefined}
        errors={[...getMessages(universesError), ...getMessages(configError), ...getMessages(giftCardError)]}
      />
    );
  }
  return <div data-testid={hasFetchedGift ? GIFT_CARD_FETCH_TEST_ID : undefined}>{children}</div>;
};

export const mapStateToProps = (state: RootState): StateProps => ({
  basketUniverse: state.basket.universe,
  proChoices: state.basket.proChoices,
  basketContent: state.basket.basketContent,
});

const mapDispatchToProps: DispatchProps = {
  setBasketContent: setBasketAttribute('basketContent'),
  setBasketAddress: setBasketAttribute('address'),
  setDuration: setBasketAttribute('duration'),
  setWeeklyRecurrence: setBasketAttribute('weekly_recurrence'),
  setProGender: setBasketAttribute('proGender'),
  resetBasket: ducks.basket.reset,
  setReferringProParams: setBasketAttribute('referringProParams'),
  setBasketGiftCard: setBasketAttribute('giftCard'),
  resetPromotion: ducks.basket.resetPromotion,
  setUniverses: ducks.universes.set,
};

export default connect(mapStateToProps, mapDispatchToProps)(Loader);
